import { useState } from "react";
import { api, useApiQuery } from "../../api";
import type { Uuid } from "../../api";
import { Field } from "../../components";
import { useAuthenticatedSession } from "../../session";
import { AsyncBoundary, PageHeader, esnFeuillePath, feuillePath } from "../../shell";
import { FeuillesFiltres } from "./FeuillesFiltres";
import type { StatutFiltre } from "./FeuillesFiltres";
import { FeuillesTable } from "./FeuillesTable";
import styles from "./Feuilles.module.css";

export type FeuillesPageProps = {
  espace: "entreprise" | "esn";
};

/**
 * La file des feuilles à valider — et, par le filtre, toutes les autres.
 *
 * Deux espaces, un écran : l'entreprise voit ses missions, l'ESN toutes, avec
 * un filtre d'entreprise en plus. Le filtre par défaut est « à valider » :
 * c'est pour ça qu'on ouvre l'écran.
 */
export function FeuillesPage({ espace }: FeuillesPageProps) {
  const { company } = useAuthenticatedSession();
  const [statut, setStatut] = useState<StatutFiltre>("submitted");
  const [entreprise, setEntreprise] = useState<Uuid | "">("");

  const query = { status: statut === "toutes" ? undefined : statut };
  const feuilles = useApiQuery(
    (o) =>
      espace === "esn"
        ? api.listEsnTimesheets({ ...query, company_id: entreprise === "" ? undefined : entreprise }, o)
        : api.listCompanyTimesheets(company?.id ?? "", query, o),
    [espace, statut, entreprise, company?.id],
  );
  const entreprises = useApiQuery((o) => (espace === "esn" ? api.listCompanies(o) : Promise.resolve(null)), [espace]);

  return (
    <div className={styles.page}>
      <PageHeader
        eyebrow={espace === "esn" ? "Espace ESN" : "Feuilles de temps"}
        title="Feuilles de temps"
        description={
          espace === "esn"
            ? "Les feuilles de tous les consultants, chez tous les clients. Approuver impute la valeur de la feuille sur la mission ; refuser la renvoie au consultant avec un motif."
            : "Les jours déclarés par les consultants sur vos missions. Approuver impute leur valeur sur l'enveloppe de la mission ; refuser renvoie la feuille avec un motif."
        }
      />

      <div className={styles.barre}>
        <FeuillesFiltres valeur={statut} onChange={setStatut} />
        {espace === "esn" && entreprises.data !== null && (
          <Field label="Entreprise">
            {(commande) => (
              <select {...commande} value={entreprise} onChange={(e) => setEntreprise(e.target.value)}>
                <option value="">Toutes</option>
                {entreprises.data?.companies.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            )}
          </Field>
        )}
      </div>

      <AsyncBoundary
        state={feuilles}
        errorTitle="Feuilles illisibles"
        skeleton={
          <FeuillesTable
            rows={[]}
            lienVers={espace === "esn" ? esnFeuillePath : feuillePath}
            montants
            entreprise={espace === "esn"}
            caption="Feuilles de temps"
            loading
            vide={{ titre: "", description: "" }}
          />
        }
      >
        {(donnees) => (
          <FeuillesTable
            rows={donnees.timesheets}
            lienVers={espace === "esn" ? esnFeuillePath : feuillePath}
            montants
            entreprise={espace === "esn"}
            caption="Feuilles de temps, les plus récentes d'abord"
            vide={
              statut === "submitted"
                ? { titre: "Rien à valider", description: "Aucune feuille n'attend de décision." }
                : { titre: "Aucune feuille", description: "Aucune feuille ne correspond à ce filtre." }
            }
          />
        )}
      </AsyncBoundary>
    </div>
  );
}
